import { useMemo, useEffect } from "react";
import { useCfmContext } from "../CfmContext";
import { getAgeFromDOB } from "../Utils/GetAgeFromDOB";
import { getValue } from "../Utils/GetValue";
import { annualise } from "../Utils/Annualise";
import isEqual from "lodash/isEqual";

/**
 * Converts the input value to a numeric value representing money.
 * If the input is already a number, it is returned as-is.
 * If the input is a string, it removes any non-numeric characters (except for '.' and '-') and parses it as a float.
 * If parsing fails or the input is invalid, returns 0.
 *
 * @param {number|string} v - The value to convert to a number.
 * @returns {number} The numeric representation of the input value.
 */
const money = (v) =>
  typeof v === "number"
    ? v
    : parseFloat(String(v || "").replace(/[^0-9.-]+/g, "")) || 0;

/**
 * Calculates income and expenditure projections for each person in the application state,
 * including gross income, taxable income, tax, expenditures, and surplus/deficit for each age.
 * 
 * Uses pension and investment results, as well as user-provided income and expenditure data,
 * to generate a detailed breakdown for each year from current age up to 100.
 * 
 * The results are memoized and dispatched to the application context when changed.
 * 
 * @function
 * @returns {Array<Object>} Array of objects, each representing a person with:
 *   - {string} name: The person's name.
 *   - {Array<Object>} incomeExpenditure: Array of yearly breakdowns with fields such as age, incomes, expenditures, tax, surplus/deficit, etc.
 *   - {string} calculatedIncome: The total income after tax at retirement age (formatted as currency).
 *   - {string} surplusAtRetirement: The surplus/deficit at retirement age (formatted as currency).
 * 
 * @example
 * const results = IncomeExpenditureCalculations();
 * // results = [
 * //   {
 * //     name: "John Doe",
 * //     incomeExpenditure: [{ age: 55, totalGrossIncome: "£...", ... }, ...],
 * //     calculatedIncome: "£...",
 * //     surplusAtRetirement: "£..."
 * //   },
 * //   ...
 * // ]
 */
const IncomeExpenditureCalculations = () => {
  const { state, dispatch } = useCfmContext();

  const pensionResults = state.pensionResults || [];
  const investmentResults = state.investmentResults || [];

  const results = useMemo(() => {
    if (
      !state?.details?.length
      // ||
      // !pensionResults?.length ||
      // !investmentResults?.length
    )
      return [];

    return state.details.map((person) => {
      const currentAge = getAgeFromDOB(person.dob) ?? 18;
      const retirementAge = getValue(person.retirementAge);
      const inflationFactor = 1 + state.inflation / 100;
      

      const incomeData =
        state.incomeData?.find(
          (i) => i.name.trim() === person.name.trim() && i.type.trim() !== "ISA"
        ) || {};
      const ageData = Array.from(
        { length: 101 - currentAge },
        (_, i) => currentAge + i
      );

      /**
       * Retrieves the regular income amount for a specific income type and age for the current person.
       *
       * @param {string} type - The type of income to filter by (e.g., "salary", "pension").
       * @param {number} age - The age at which to check for the income.
       * @returns {number} The annualised income amount for the specified type and age, or 0 if not found.
       *
       * @example
       * // Returns the annualised salary income for age 35
       * getRegularIncomeByType('salary', 35);
       */
      const getRegularIncomeByType = (type, age) => {
        const row = state.income?.find(
          (r) =>
            r.name.trim() === person.name.trim() &&
            r.type.trim() === type.trim() &&
            +r.start <= age &&
            +r.end >= age
        );
        return money(
          annualise({
            value: row?.amount?.value,
            unit: row?.amount?.unit,
          })
        );
      };

      const prevValues = {
        pensionRegularIncome: 0,
        investmentGIARegularIncome: 0,
        investmentISARegularIncome: 0,
        taxedDrawdown: 0,
        btlRental: 0,
        dbPension: 0,
        statePension: 0,
        salary: 0,
        retirementExpenditure: 0,
        otherIncome: 0,
        regularExpenditure: 0,
        targetIncome: 0,
      };

      const personResults = [];

      ageData.forEach((age, index) => {
        const allPensionRows = pensionResults
          .filter((res) => res.name.trim() === person.name.trim())
          .flatMap(
            (res) => res.pensionFunds?.filter((row) => row.age === age) || [],
          );

        const allInvestmentRows = investmentResults
          .filter((res) => res.name.trim() === person.name.trim())
          .flatMap(
            (res) =>
              res.investmentFunds?.filter((row) => row.age === age) || {},
          );
        const allGIAInvestmentRows = investmentResults
          .filter(
            (res) =>
              res.name.trim() === person.name.trim() &&
              res.type.trim() !== "ISA",
          )
          .flatMap(
            (res) =>
              res.investmentFunds?.filter((row) => row.age === age) || {},
          );
        const allISAInvestmentRows = investmentResults
          .filter(
            (res) =>
              res.name.trim() === person.name.trim() &&
              res.type.trim() === "ISA",
          )
          .flatMap(
            (res) =>
              res.investmentFunds?.filter((row) => row.age === age) || {},
          );

        const pensionRegularIncomeBase = allPensionRows.reduce(
          (sum, row) => sum + money(row.taxedDrawdown),
          0,
        );

        // console.log("Pension Regular Income Base:", pensionRegularIncomeBase);

        const tfls = allPensionRows.reduce(
          (sum, row) => sum + money(row.tflsWithdrawal) + money(row.phasedTfc),
          0,
        );
        const pensionAdhocWithdrawal = allPensionRows.reduce(
          (sum, row) => sum + money(row.adhocWithdrawal),
          0,
        );
        const pensionAdhocContribution = allPensionRows.reduce(
          (sum, row) => sum + money(row.adhocContribution),
          0,
        );

        const investmentGIARegularIncome = allGIAInvestmentRows.reduce(
          (sum, row) => sum + money(row.regularWithdrawals),
          0,
        );
        const investmentISARegularIncome = allISAInvestmentRows.reduce(
          (sum, row) => sum + money(row.regularWithdrawals),
          0,
        );
        const investmentAdhocContribution = allInvestmentRows.reduce(
          (sum, row) => sum + money(row.adhocInvestment),
          0,
        );
        const investmentAdhocWithdrawal = allInvestmentRows.reduce(
          (sum, row) => sum + money(row.adhocWithdrawals),
          0,
        );

        // const statePension = getRegularIncomeByType("State Pension", age);
        // const dbPension = getRegularIncomeByType("DB Pension", age);
        // const btlRental = getRegularIncomeByType("Rental Income", age);
        // const otherIncome = getRegularIncomeByType("Other Income", age);

        const statePensionBase = getRegularIncomeByType("State Pension", age);

        // console.log("State Pension Base:", statePensionBase);
        const dbPensionBase = getRegularIncomeByType("DB Pension", age);
        const btlRentalBase = getRegularIncomeByType("Rental Income", age);
        const otherIncomeBase = getRegularIncomeByType("Other Income", age);

        const salaryBase =
          age >= currentAge && age < retirementAge
            ? annualise({
                value: getValue(person.salary.value),
                unit: person.salary?.unit,
              })
            : 0;

        const retirementExpenditureBase = annualise({
          value: getValue(person.retirementExpenditure),
          unit: person.retirementExpenditure?.unit,
        });

        // console.log(retirementExpenditureBase);

        const regularExpenditureBase =
          age < retirementAge
            ? annualise({
                value: getValue(person.currentExpenditure),
                unit: person.currentExpenditure?.unit,
              })
            : annualise({
                value: getValue(person.retirementExpenditure),
                unit: person.retirementExpenditure?.unit,
              });

        const targetIncomeBase =
          age < retirementAge ? 0 : getValue(incomeData.targetIncome);

        prevValues.investmentGIARegularIncome = investmentGIARegularIncome
          ? prevValues.investmentGIARegularIncome
            ? prevValues.investmentGIARegularIncome * inflationFactor
            : investmentGIARegularIncome
          : 0;
        prevValues.investmentISARegularIncome = investmentISARegularIncome
          ? prevValues.investmentISARegularIncome
            ? prevValues.investmentISARegularIncome * inflationFactor
            : investmentISARegularIncome
          : 0;

        // prevValues.pensionRegularIncome =
        //   currentAge === retirementAge
        //     ? pensionRegularIncomeBase
        //     : pensionRegularIncomeBase
        //       ? pensionRegularIncomeBase
        //       : prevValues.pensionRegularIncome * inflationFactor
        //         ? prevValues.pensionRegularIncome * inflationFactor
        //         : pensionRegularIncomeBase;

        prevValues.pensionRegularIncome =
          pensionRegularIncomeBase > 0 ? pensionRegularIncomeBase : 0;

        // prevValues.pensionRegularIncome =
        //   currentAge === retirementAge
        //     ? pensionRegularIncomeBase
        //     : pensionRegularIncomeBase
        //     ? prevValues.pensionRegularIncome * inflationFactor
        //       ? prevValues.pensionRegularIncome * inflationFactor
        //       : pensionRegularIncomeBase
        //     : pensionRegularIncomeBase;

        prevValues.btlRental = btlRentalBase
          ? prevValues.btlRental * inflationFactor
            ? prevValues.btlRental * inflationFactor
            : btlRentalBase
          : btlRentalBase;

        prevValues.dbPension = dbPensionBase
          ? prevValues.dbPension * inflationFactor
            ? prevValues.dbPension * inflationFactor
            : dbPensionBase
          : dbPensionBase;

        prevValues.statePension = statePensionBase
          ? prevValues.statePension
            ? prevValues.statePension * inflationFactor
            : statePensionBase
          : 0;

        prevValues.salary =
          index === 0
            ? salaryBase
            : age < retirementAge
              ? prevValues.salary * inflationFactor
              : 0;
        // prevValues.salary * inflationFactor;

        prevValues.otherIncome = otherIncomeBase
          ? prevValues.otherIncome * inflationFactor
            ? prevValues.otherIncome * inflationFactor
            : otherIncomeBase
          : otherIncomeBase;

        prevValues.regularExpenditure =
          index === 0
            ? regularExpenditureBase
            : age < retirementAge
              ? prevValues.regularExpenditure * inflationFactor
              : prevValues.retirementExpenditure * inflationFactor;

        prevValues.retirementExpenditure =
          index === 0
            ? retirementExpenditureBase
            : prevValues.retirementExpenditure * inflationFactor;

        prevValues.targetIncome =
          index === 0
            ? targetIncomeBase
            : prevValues.targetIncome * inflationFactor;

        const adhocIncome =
          incomeData.adhocIncome?.find((item) => +item.age === age)?.amount ||
          0;

        const adhocExpenditure =
          incomeData.adhocExpense?.find((item) => +item.age === age)?.amount ||
          0;

        const targetIncome =
          age < retirementAge ? 0 : getValue(incomeData.targetIncome);

        const totalGrossIncome =
          prevValues.pensionRegularIncome +
          tfls +
          prevValues.taxedDrawdown +
          -pensionAdhocWithdrawal +
          pensionAdhocContribution +
          prevValues.investmentGIARegularIncome +
          investmentAdhocContribution +
          -investmentAdhocWithdrawal +
          prevValues.btlRental +
          prevValues.dbPension +
          prevValues.statePension +
          prevValues.salary +
          prevValues.otherIncome +
          adhocIncome;

        const taxableIncome = totalGrossIncome - tfls;

        const personalAllowance = 12570;
        const basicRateThreshold = 50270;
        const higherRateThreshold = 125140;

        let totalTax = 0;
        if (taxableIncome > personalAllowance) {
          let remainingIncome = taxableIncome - personalAllowance;

          if (remainingIncome <= basicRateThreshold - personalAllowance) {
            totalTax += remainingIncome * 0.2;
          } else {
            totalTax += (basicRateThreshold - personalAllowance) * 0.2;
            remainingIncome -= basicRateThreshold - personalAllowance;

            if (remainingIncome <= higherRateThreshold - basicRateThreshold) {
              totalTax += remainingIncome * 0.4;
            } else {
              totalTax += (higherRateThreshold - basicRateThreshold) * 0.4;
              remainingIncome -= higherRateThreshold - basicRateThreshold;

              if (remainingIncome > 0) {
                totalTax += remainingIncome * 0.45;
              }
            }
          }
        }

        const totalIncomeAfterTax =
          totalGrossIncome - totalTax + prevValues.investmentISARegularIncome;
        const totalExpenditure =
          prevValues.regularExpenditure + adhocExpenditure;
        const surplusDeficit = totalIncomeAfterTax - totalExpenditure;
        personResults.push({
          age,
          pensionRegularIncome: `£${prevValues.pensionRegularIncome.toFixed(
            2,
          )}`,
          investmentGIARegularIncome: `£${prevValues.investmentGIARegularIncome.toFixed(
            2,
          )}`,
          investmentISARegularIncome: `£${prevValues.investmentISARegularIncome.toFixed(
            2,
          )}`,
          taxedDrawdown: `£${prevValues.taxedDrawdown.toFixed(2)}`,
          btlRental: `£${prevValues.btlRental.toFixed(2)}`,
          dbPension: `£${prevValues.dbPension.toFixed(2)}`,
          statePension: `£${prevValues.statePension.toFixed(2)}`,
          salary: `£${prevValues.salary.toFixed(2)}`,
          otherIncome: `£${prevValues.otherIncome.toFixed(2)}`,
          regularExpenditure: `£${prevValues.regularExpenditure.toFixed(2)}`,
          retirementExpenditure: `£${prevValues.retirementExpenditure.toFixed(
            2,
          )}`,
          adhocIncome: `£${adhocIncome.toFixed(2)}`,
          targetIncome: `£${prevValues.targetIncome.toFixed(2)}`,
          totalGrossIncome: `£${totalGrossIncome.toFixed(2)}`,
          taxableIncome: `£${taxableIncome.toFixed(2)}`,
          totalIncomeAfterTax: `£${totalIncomeAfterTax.toFixed(2)}`,
          totalExpenditure: `£${totalExpenditure.toFixed(2)}`,
          surplusDeficit: `£${surplusDeficit.toFixed(2)}`,
          adhocIncome: `£${adhocIncome.toFixed(2)}`,
          adhocExpenditure: `£${adhocExpenditure.toFixed(2)}`,
          pensionAdhocWithdrawal: `£${pensionAdhocWithdrawal.toFixed(2)}`,
          pensionAdhocContribution: `£${pensionAdhocContribution.toFixed(2)}`,
          investmentAdhocContribution: `£${investmentAdhocContribution.toFixed(
            2,
          )}`,
          investmentAdhocWithdrawal: `£${investmentAdhocWithdrawal.toFixed(2)}`,
          investmentGIARegularIncome: `£${prevValues.investmentGIARegularIncome.toFixed(
            2,
          )}`,
          investmentRegularIncome: `£${(
            Number(prevValues.investmentGIARegularIncome) +
            Number(prevValues.investmentISARegularIncome)
          ).toFixed(2)}`,

          taxFreeCash: `£${tfls.toFixed(2)}`,
        });
      });

      const incomeAtRetirement =
        personResults.find((row) => row.age === retirementAge)
          ?.totalIncomeAfterTax || "£0.00";
      
      const surplusAtRetirement =
        personResults.find((row) => row.age === retirementAge)
          ?.surplusDeficit || "£0.00";

      
   

      return {
        name: person.name,
        incomeExpenditure: personResults,
        calculatedIncome: incomeAtRetirement,
        surplusAtRetirement,
      };
    });
  }, [state]);

  useEffect(() => {
    const existing = state.incomeExpenditureResults || [];

    const isDifferent = !isEqual(existing, results);

    if (results.length && isDifferent) {
      dispatch({ type: "SET_INCOME_EXPENDITURE_RESULTS", payload: results });
    }
  }, [results, state.incomeExpenditureResults, dispatch]);

  useEffect(() => {
    const calculatedIncomeArray = results.map((r) => ({
      name: r.name,
      value: r.calculatedIncome,
    }));

    const existingCalculated = state.calculatedIncome || [];

    const isDiff = !isEqual(calculatedIncomeArray, existingCalculated);

    if (results.length && isDiff) {
      dispatch({
        type: "SET_CALCULATED_INCOME",
        payload: calculatedIncomeArray,
      });
    }
  }, [results, state.calculatedIncome, dispatch]);

  return results;
};

export default IncomeExpenditureCalculations;
